import * as React from "react";

import "./image-viewer.scss";

import {FontAwesomeIcon}    from "@fortawesome/react-fontawesome";
import {cx}                 from "emotion";

import {ComponentLink, ComponentNext, Link, makeChain, valueHelper} from "@Common/react/cst2";
import {Responsive}                                                 from "@Common/ui-components/responsive";
import {UseState}                                                   from "@Common/react/use-state";
import {cs}                                                         from "@Common/react/chain-services";
import {keyed}                                                      from "@Common/react/keyed";
import {Load2}                                                      from "@Common/react/load2";

import {consumeContext}         from "@Xpresso/loaders/consume-context";
import {TemplateSvgThumbnail}   from "@Xpresso/routes/common/components/template-svg-thumbnail/template-svg-thumbnail";

import {decreaseGallery, increaseGallery}   from "../front-template-viewer";

import {ImageZoom} from "./image-zoom/image-zoom";

type Image = {url: string, onClick: () => void, selected?: boolean, outOfStock?: boolean};

export const ImageViewer = ({images, gallery, loading, onEnlarge, viewing}:{
    gallery     : ComponentNext<typeof UseState<{start: number, end: number}>>,
    images     ?: Image[],
    loading    ?: boolean,
    onEnlarge   : () => void,
    viewing     : number,
}) => (
    makeChain<JSX.Element>()
    .then(...consumeContext("svgThumbnailsCache"))
    .then("hovered", ({}, next: ComponentLink<typeof UseState<boolean>>) => UseState({next}))
    .then("responsive", ({}, next: Link<JSX.Element, any>) => <Responsive {...{
        sizes: {
            md: 1024,
        },
        next,
    }}/>)
    .then("img", valueHelper(() => images?.[viewing] ?? null))
    .then("imgSvgCodeLoad", ({img, svgThumbnailsCache}, next: ComponentLink<typeof Load2<string>>) => (
        Load2({
            next,
            _key: img?.url,
            fetch: async () => img?.url.endsWith(".svg") ? svgThumbnailsCache.loadUrl(img.url) : null
        })
    ))
    .then(({img, imgSvgCodeLoad: {value: imgSvgCode}, responsive}) => (
        <div className="image-viewer image-viewer-f99">
            <div className="large">
                <div className="img"><ImageZoom 
                    renderImage={() => imgSvgCode ? (
                        <TemplateSvgThumbnail svgCode={imgSvgCode}/>
                    ) : (
                        <img 
                            src={loading ? (
                                require("../../../../../../common/loading_pdf.gif")
                            ) : !img ? (
                                "assets/NoImageAvailable.png"
                            ) : (
                                img.url
                            )}
                            alt={`Image ${viewing}`}
                            xd-qa-type='Image'
                            xd-qa-name={`Image ${viewing}`}
                        />
                    )}
                /></div>

                <div className="footer">
                    {responsive.ge("md") && !loading &&
                        <div className="right">
                            <div className="enlarge" onClick={onEnlarge} xd-qa-type="button" xd-qa-name="Click to Enlarge">
                                <i className="fa fa-search"/>
                                <div className="text">
                                    Click to Enlarge
                                </div>
                            </div>
                        </div>
                    }
                </div>
            </div>
            {images?.length > 1 && thumbnails(images, gallery, viewing)}
        </div>
    ))
    .run()
);

const thumbnails = (
    images: Image[],
    gallery: ComponentNext<typeof UseState<{start: number, end: number}>>,
    viewing: number,
) => {
    const imageIndexMax = images?.length - 1 || 0
    return cs(
        ({}) => (
            <div className="thumbnails">
                <div className="arrow" xd-qa-type="button" xd-qa-name="Back Arrow">
                    <FontAwesomeIcon
                        icon={require('@fortawesome/free-solid-svg-icons')['faCaretDown']}
                        className={cx("icon-caret", {hidden: imageIndexMax === 0 || viewing === 0})}
                        onClick={(e) => {
                            e.stopPropagation()
                            const previous = viewing > 0 ? viewing - 1 : imageIndexMax
                            images[previous].onClick()
                            decreaseGallery(gallery, images, viewing)
                        }}
                    />
                </div>

                {images?.map((img, i) => {
                    if(i === gallery.value.end + 1 || i === gallery.value.end + 2 || i === gallery.value.end + 3) {
                        return <img
                            className="hidden"
                            onClick={() => img.onClick()}
                            src={img.url}
                            xd-qa-type="icon"
                            xd-qa-name={`Image${i + 1}`}
                        />
                    }
                    if(i === gallery.value.start - 1 || i === gallery.value.start - 2 || i === gallery.value.start - 3) {
                        return <img
                            className="hidden"
                            onClick={() => img.onClick()}
                            src={img.url}
                            xd-qa-type="icon"
                            xd-qa-name={`Image${i + 1}`}
                        />
                    }
                    if(i < gallery.value.start) {
                        return
                    }
                    if(i > gallery.value.end) {
                        return
                    }
                    return cs(
                        keyed(i),
                        ["loading", ({}, next) => UseState({
                            initValue: true,
                            next
                        })],
                        ({loading}) => (
                            <div 
                                className="img-container"
                                onClick={() => img.onClick()}
                            >
                                {images.length > 1 && i === viewing && (
                                    <div className="highlight"/>
                                )}
                                <div
                                    className={cx("img", {frontTemplate: images.length > 1 && i === viewing})}
                                >
                                    <img
                                        className={cx("small-image", {loading: loading.value})}
                                        src={loading.value ? require("../../../../../../common/loading_pdf.gif") : img.url}
                                        xd-qa-type="image"
                                        xd-qa-name={`Image${i + 1}`}
                                        onLoad={() => loading.onChange(false)}
                                    />
                                    {img.selected && (
                                        <div className="check">
                                            <i className="fa fa-check"/>
                                        </div>
                                    )}                                
                                    {img.outOfStock && (
                                        <img
                                            className="out-of-stock-overlay"
                                            src={require("../../../../../../../../public/assets/out-of-stock-overlay.svg")}
                                            xd-qa-type="image"
                                            xd-qa-name="Out of Stock Image"
                                        />
                                    )}
                                </div>
                            </div>
                        )
                    )
                })}
                <div className="arrow" xd-qa-type="button" xd-qa-name="Forward Arrow">
                    <FontAwesomeIcon
                        icon={require('@fortawesome/free-solid-svg-icons')['faCaretUp']}
                        className={cx("icon-caret", {hidden: viewing === imageIndexMax})}
                        onClick={(e) => {
                            e.stopPropagation()
                            const subsequent = viewing < imageIndexMax ? viewing + 1 : 0
                            images[subsequent].onClick()
                            increaseGallery(gallery, images, viewing)
                        }}
                    />
                </div>
            </div>
        )
    )
}

